import type { CatalogIngredient } from './api/catalog.ts';
import {
  STORE_CATEGORIES,
  type CatalogFacts,
  type PackSize,
  type StoreCategory,
} from './catalog.ts';
import { addDays, isoWeekday } from './dates.ts';
import { lineGrams } from './grams.ts';
import type { ShoppingCheckState } from './planning.ts';
import { scaleAmount, scaleFactor, type Amount } from './quantity.ts';
import { catalogFacts } from './recipe-figures.ts';
import { UNITS, type Unit } from './units.ts';

/**
 * The shopping list (PLAN.md §8): cook sessions in a date range, plus recipes put on the list
 * without a day → their ingredient lines scaled to the portions → merged per ingredient and unit dimension (200 g + 0,3 kg = 500 g;
 * "2 onions" + "100 g onion" = 3 onions ≈ 320 g) → pack-rounded cost estimate → staples set
 * aside unless running low → grouped by store section. Pure: the device computes it offline
 * from the mirror, the server gets the same answer.
 */

/** A catalog entry as the list needs it, with the household's view. */
export interface ShoppingIngredient extends CatalogFacts {
  nameDe: string;
  nameEn: string;
  category: StoreCategory;
  isStaple: boolean;
  runningLow: boolean;
}

/** A synced catalog entry (with the household's view) as the list needs it. */
export function toShoppingIngredient(entry: CatalogIngredient): ShoppingIngredient {
  return {
    ...catalogFacts(entry),
    nameDe: entry.nameDe,
    nameEn: entry.nameEn,
    category: entry.category,
    isStaple: entry.household.isStaple,
    runningLow: entry.household.runningLow,
  };
}

export interface ShoppingLine extends Amount {
  id: string;
  recipeId: string;
  name: string;
  ingredientId: string | null;
  /** Stored weight of the line as written (import or fix), if any. */
  grams: number | null;
  optional: boolean;
}

export interface ShoppingSession {
  id: string;
  recipeId: string;
  date: string;
  portions: number;
  /** Set once it was cooked: bought and used, so off the list. */
  doneAt?: string | Date | null;
}

/** A recipe put on the list without planning it: bought now, cooked whenever. */
export interface ShoppingRecipeEntry {
  id: string;
  recipeId: string;
  portions: number;
}

export interface ShoppingManualItem extends Amount {
  id: string;
  text: string;
  ingredientId: string | null;
  category: StoreCategory;
  checkedAt?: string | null;
}

export interface ShoppingInput {
  /** First and last day, inclusive (`YYYY-MM-DD`). */
  range: { start: string; end: string };
  sessions: readonly ShoppingSession[];
  recipes: ReadonlyMap<string, { yieldPortions: number }>;
  lines: readonly ShoppingLine[];
  catalog: ReadonlyMap<string, ShoppingIngredient>;
  manual?: readonly ShoppingManualItem[];
  /** Recipes on the list without a cook session; they don't depend on the range. */
  recipeEntries?: readonly ShoppingRecipeEntry[];
  /** Only these recipes' lines, without manual items (shopping for some recipes only). */
  recipeFilter?: ReadonlySet<string> | null;
  /**
   * The range's checks by item key: a whole item (`ShoppingItem.key`) or one recipe's share of
   * it (`recipeCheckKey`). Without them every item is open with its full amount.
   */
  checks?: ReadonlyMap<string, ShoppingCheckState>;
  language: 'de' | 'en';
}

/** Where part of an item comes from. */
export interface ShoppingSource {
  /** The cook session, or null for a recipe put on the list without a day. */
  sessionId: string | null;
  /** The list entry (`ShoppingRecipeEntry`), or null for a cook session. */
  entryId: string | null;
  recipeId: string;
  lineId: string;
  amount: Amount;
}

export interface ShoppingItem {
  /** Stable across regenerations: `ingredient:<id>:<dim>`, `line:<name>:<dim>`, `manual:<id>`. */
  key: string;
  /** The ingredient or unmatched name whatever the unit (`ingredient:<id>`, `line:<name>`). */
  group: string;
  name: string;
  ingredientId: string | null;
  category: StoreCategory;
  manual: boolean;
  /** Checked off or "have it": the item as a whole, or every recipe's share of it. */
  state: ShoppingCheckState | null;
  /** Some recipes' shares are checked off: amount, cost and sources are what is left. */
  partly: boolean;
  /** What to buy; null when no line says how much ("Salz nach Geschmack"). */
  amount: Amount | null;
  /** Total weight when known ("≈ 320 g"). */
  grams: number | null;
  /** Units were bridged with estimated weights. */
  approx: boolean;
  /** Packs to buy when the catalog knows the pack size. */
  packs: { count: number; size: PackSize } | null;
  /** Estimated price of what is bought (whole packs when known). */
  cost: number | null;
  /** Every line behind it is optional. */
  optional: boolean;
  staple: boolean;
  runningLow: boolean;
  sources: ShoppingSource[];
}

export interface ShoppingGroup {
  category: StoreCategory;
  items: ShoppingItem[];
}

export interface ShoppingList {
  /** Everything to buy, in store order. */
  groups: ShoppingGroup[];
  /** Staples the recipes use but the household has at home (not running low). */
  staples: ShoppingItem[];
  /** Sum of the priced items (an estimate, "≈ 23 €"); null when nothing is priced. */
  total: number | null;
  /** Items to buy without a price. */
  unpriced: number;
}

/**
 * The check of one recipe's share of an item (shopping for some recipes only): checking onions
 * for the curry leaves the soup's onions on the list.
 */
export const recipeCheckKey = (recipeId: string, group: string): string =>
  `recipe:${recipeId}:${group}`;

/** Lowercase, accents folded, whitespace collapsed: "Zwiebeln " and "zwiebeln" merge. */
export const normalizeName = (name: string): string =>
  name.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').replace(/\s+/g, ' ').trim();

/** The dimension token of an amount: `mass`, `volume`, or the count unit (`piece`, `can` …). */
function dimOf(unit: Unit | null): string {
  if (!unit) return 'piece';
  const { dimension } = UNITS[unit];
  return dimension === 'mass' || dimension === 'volume' ? dimension : unit;
}

const middle = (a: Amount): number | null =>
  a.quantity === null
    ? null
    : a.quantityMax != null
      ? (a.quantity + a.quantityMax) / 2
      : a.quantity;

interface Part {
  source: ShoppingSource;
  dim: string | null;
  /** Quantity in the dimension's base (g, ml, or the count unit). */
  base: number | null;
  grams: number | null;
  guessed: boolean;
  optional: boolean;
}

function toPart(
  source: ShoppingSource,
  stored: number | null,
  facts: ShoppingIngredient | undefined,
  optional: boolean,
): Part {
  const { amount } = source;
  const q = middle(amount);
  if (q === null) {
    return { source, dim: null, base: null, grams: stored, guessed: false, optional };
  }
  const unit = amount.unit;
  const dim = dimOf(unit);
  const base = unit && (dim === 'mass' || dim === 'volume') ? q * UNITS[unit].factor : q;
  let grams = stored;
  let guessed = false;
  if (grams === null && facts) {
    const weighed = lineGrams(amount, facts);
    if (weighed) {
      grams = weighed.grams;
      guessed = weighed.basis !== 'measured';
    }
  } else if (grams === null && dim === 'mass') {
    grams = base;
  }
  return { source, dim, base, grams, guessed, optional };
}

/** Rounds up to whole pieces (cans, bunches …): nobody buys 1.4 onions. */
const wholeUp = (n: number) => Math.max(1, Math.ceil(n - 1e-6));
const round1 = (n: number) => Math.round(n * 10) / 10;

function amountOf(dim: string, base: number): Amount {
  if (dim === 'mass') return { quantity: round1(base), unit: 'g' };
  if (dim === 'volume') return { quantity: round1(base), unit: 'ml' };
  const unit = dim as Unit;
  // Spoon-like counts (pinch, dash) stay as they are; buyable pieces round up.
  const whole = !['pinch', 'knifetip', 'dash', 'handful', 'sprig', 'leaf', 'slice'].includes(unit);
  return { quantity: whole ? wholeUp(base) : round1(base), unit: unit === 'piece' ? null : unit };
}

/** Packs to buy and their cost, or the cost of the amount itself without a pack size. */
function priceItem(
  item: Pick<ShoppingItem, 'amount' | 'grams'>,
  dim: string | null,
  facts: ShoppingIngredient,
): { packs: ShoppingItem['packs']; cost: number | null } {
  const q = item.amount?.quantity ?? null;
  // Counted items (pieces, cans, bunches) are priced per piece.
  const pieces =
    dim !== null && dim !== 'mass' && dim !== 'volume' && q !== null
      ? q
      : item.grams !== null && facts.unitGrams.piece
        ? item.grams / facts.unitGrams.piece
        : null;
  const ml =
    dim === 'volume' && q !== null
      ? q
      : item.grams !== null && facts.densityGPerMl
        ? item.grams / facts.densityGPerMl
        : null;

  let packs: ShoppingItem['packs'] = null;
  let boughtGrams = item.grams;
  let boughtPieces = pieces;
  const pack = facts.packSize;
  if (pack) {
    const packDim = UNITS[pack.unit].dimension;
    const perPack = pack.qty * UNITS[pack.unit].factor;
    let need: number | null = null;
    let packGrams: number | null = null;
    if (packDim === 'mass') {
      need = item.grams;
      packGrams = perPack;
    } else if (packDim === 'volume') {
      need = ml;
      packGrams = facts.densityGPerMl ? perPack * facts.densityGPerMl : perPack;
    } else if (pack.unit === 'piece') {
      need = pieces;
      packGrams = facts.unitGrams.piece ? pack.qty * facts.unitGrams.piece : null;
    }
    if (need !== null && need > 0 && perPack > 0) {
      const count = wholeUp(need / perPack);
      packs = { count, size: pack };
      boughtGrams = packGrams !== null ? count * packGrams : item.grams;
      boughtPieces = pack.unit === 'piece' ? count * pack.qty : pieces;
    }
  }
  let cost: number | null = null;
  if (boughtGrams !== null && facts.priceEurPerKg !== null) {
    cost = (boughtGrams / 1000) * facts.priceEurPerKg;
  } else if (boughtPieces !== null && facts.priceEurPerPiece !== null) {
    cost = wholeUp(boughtPieces) * facts.priceEurPerPiece;
  }
  return { packs, cost };
}

/**
 * Parts of one ingredient (or one unmatched name) → items. One dimension: summed. Several: when
 * every part can be weighed they merge (in pieces when the recipes count pieces and a piece
 * weight is known, else in grams); otherwise each dimension stays its own item.
 */
function mergeParts(parts: Part[]): { dim: string | null; parts: Part[]; merged: boolean }[] {
  const quantified = parts.filter((p) => p.dim !== null);
  const loose = parts.filter((p) => p.dim === null);
  const dims = [...new Set(quantified.map((p) => p.dim as string))];
  if (dims.length === 0) return [{ dim: null, parts, merged: false }];
  if (dims.length === 1) return [{ dim: dims[0] ?? null, parts, merged: false }];
  if (quantified.every((p) => p.grams !== null)) {
    const dim = dims.includes('piece') ? 'piece' : 'mass';
    return [{ dim, parts, merged: true }];
  }
  return dims.map((dim, i) => ({
    dim,
    parts: [...quantified.filter((p) => p.dim === dim), ...(i === 0 ? loose : [])],
    merged: false,
  }));
}

export function buildShoppingList(input: ShoppingInput): ShoppingList {
  const { range, catalog, language, recipeFilter } = input;
  const checks = input.checks ?? new Map<string, ShoppingCheckState>();
  const linesByRecipe = new Map<string, ShoppingLine[]>();
  for (const line of input.lines) {
    linesByRecipe.set(line.recipeId, [...(linesByRecipe.get(line.recipeId) ?? []), line]);
  }
  const shown = (recipeId: string) => !recipeFilter || recipeFilter.has(recipeId);
  const cooked = [
    ...input.sessions
      .filter((s) => s.date >= range.start && s.date <= range.end && !s.doneAt)
      .map((s) => ({ ...s, sessionId: s.id, entryId: null })),
    ...(input.recipeEntries ?? []).map((e) => ({ ...e, sessionId: null, entryId: e.id })),
  ].filter((c) => shown(c.recipeId));

  // Bucket every scaled line by catalog ingredient, or by its name when unmatched.
  const buckets = new Map<string, { ingredientId: string | null; name: string; parts: Part[] }>();
  for (const { recipeId, portions, sessionId, entryId } of cooked) {
    const recipe = input.recipes.get(recipeId);
    if (!recipe) continue;
    const factor = scaleFactor(recipe.yieldPortions, portions);
    for (const line of linesByRecipe.get(recipeId) ?? []) {
      const facts = line.ingredientId ? catalog.get(line.ingredientId) : undefined;
      const bucketKey = facts ? `ingredient:${facts.id}` : `line:${normalizeName(line.name)}`;
      const amount = scaleAmount(
        { quantity: line.quantity, quantityMax: line.quantityMax ?? null, unit: line.unit },
        factor,
      );
      const source = { sessionId, entryId, recipeId, lineId: line.id, amount };
      const stored = line.grams === null ? null : line.grams * factor;
      const bucket = buckets.get(bucketKey) ?? {
        ingredientId: facts ? facts.id : null,
        name: facts ? (language === 'de' ? facts.nameDe : facts.nameEn) : line.name.trim(),
        parts: [],
      };
      bucket.parts.push(toPart(source, stored, facts, line.optional));
      buckets.set(bucketKey, bucket);
    }
  }

  const items: ShoppingItem[] = [];
  for (const [bucketKey, bucket] of buckets) {
    const facts = bucket.ingredientId ? catalog.get(bucket.ingredientId) : undefined;
    for (const group of mergeParts(bucket.parts)) {
      const { dim, merged } = group;
      const key = `${bucketKey}:${dim ?? 'any'}`;
      // Checked as a whole, or share by share (one recipe at a time): the amount is what's left.
      const whole = checks.get(key) ?? null;
      const shareOf = (p: Part) => checks.get(recipeCheckKey(p.source.recipeId, bucketKey));
      const open = whole ? [] : group.parts.filter((p) => !shareOf(p));
      const parts = open.length > 0 ? open : group.parts;
      const state =
        whole ??
        (open.length > 0
          ? null
          : group.parts.every((p) => shareOf(p) === 'have_it')
            ? 'have_it'
            : 'checked');
      const quantified = parts.filter((p) => p.dim !== null);
      const weighable = quantified.length > 0 && quantified.every((p) => p.grams !== null);
      const grams = weighable
        ? round1(quantified.reduce((sum, p) => sum + (p.grams ?? 0), 0))
        : null;
      let amount: Amount | null = null;
      if (dim !== null && quantified.length > 0) {
        if (merged && dim === 'piece' && grams !== null && facts?.unitGrams.piece) {
          amount = amountOf('piece', grams / facts.unitGrams.piece);
        } else if (merged) {
          amount = amountOf('mass', grams ?? 0);
        } else {
          amount = amountOf(
            dim,
            quantified.reduce((sum, p) => sum + (p.base ?? 0), 0),
          );
        }
      }
      const item: ShoppingItem = {
        key,
        group: bucketKey,
        name: bucket.name,
        ingredientId: bucket.ingredientId,
        category: facts?.category ?? 'other',
        manual: false,
        state,
        partly: open.length > 0 && open.length < group.parts.length,
        amount,
        grams: amount ? grams : null,
        approx: merged || quantified.some((p) => p.guessed),
        packs: null,
        cost: null,
        optional: parts.every((p) => p.optional),
        staple: facts?.isStaple ?? false,
        runningLow: facts?.runningLow ?? false,
        sources: parts.map((p) => p.source),
      };
      if (facts && item.amount) Object.assign(item, priceItem(item, dim, facts));
      items.push(item);
    }
  }

  for (const manual of recipeFilter ? [] : (input.manual ?? [])) {
    items.push({
      key: `manual:${manual.id}`,
      group: `manual:${manual.id}`,
      name: manual.text,
      ingredientId: manual.ingredientId,
      category: manual.category,
      manual: true,
      state: manual.checkedAt ? 'checked' : null,
      partly: false,
      amount: manual.quantity === null ? null : { quantity: manual.quantity, unit: manual.unit },
      grams: null,
      approx: false,
      packs: null,
      cost: null,
      optional: false,
      staple: false,
      runningLow: false,
      sources: [],
    });
  }

  const toBuy = items.filter((i) => !i.staple || i.runningLow);
  const staples = items.filter((i) => i.staple && !i.runningLow);
  const collator = new Intl.Collator(language === 'de' ? 'de-DE' : 'en-GB');
  const groups = STORE_CATEGORIES.map((category) => ({
    category,
    items: toBuy
      .filter((i) => i.category === category)
      .sort((a, b) => collator.compare(a.name, b.name) || a.key.localeCompare(b.key)),
  })).filter((g) => g.items.length > 0);
  const priced = toBuy.filter((i) => i.cost !== null);
  return {
    groups,
    staples: staples.sort((a, b) => collator.compare(a.name, b.name)),
    total: priced.length > 0 ? priced.reduce((sum, i) => sum + (i.cost ?? 0), 0) : null,
    unpriced: toBuy.filter((i) => i.cost === null && !i.manual).length,
  };
}

/**
 * The default range (PLAN.md §8): from today until the day before the next shopping day (on the
 * shopping day itself: the week ahead), or a number of days from today.
 */
export function defaultShoppingRange(
  today: string,
  prefs: { rangeMode: 'days' | 'until_shopping_day'; rangeDays: number; shoppingWeekday: number },
): { start: string; end: string } {
  if (prefs.rangeMode === 'days') {
    return { start: today, end: addDays(today, Math.max(1, prefs.rangeDays) - 1) };
  }
  const ahead = (prefs.shoppingWeekday - isoWeekday(today) + 7) % 7 || 7;
  return { start: today, end: addDays(today, ahead - 1) };
}
